import { useEffect, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, ExternalLink, Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { publishLandingPage, unpublishLandingPage } from '@/lib/apis';
import { toast } from '@/hooks/useToast';
import type { LandingPage } from '@/lib/types';
import { VibeEditChat } from './vibe-edit-chat';
import { LeadsTable } from './leads-table';

interface LandingPageViewProps {
  landingPage: LandingPage;
  organizationId: number;
  onChanged: () => void;
}

export function LandingPageView({ landingPage, organizationId, onChanged }: LandingPageViewProps) {
  const isPublished = landingPage.status === 'PUBLISHED';
  const queryClient = useQueryClient();

  // The preview iframe points at a stable URL (preview/{id}/ or p/{id}/). When an edit finishes, the
  // rebuilt page sits at that same URL, so bump a cache-buster to force the iframe to reload it (defeats
  // the browser + lp-server 5-min cache) and pull the assistant's reply into the thread.
  const [previewVersion, setPreviewVersion] = useState(0);
  const wasEditing = useRef(landingPage.edit_in_progress);
  useEffect(() => {
    if (wasEditing.current && !landingPage.edit_in_progress) {
      setPreviewVersion((version) => version + 1);
      queryClient.invalidateQueries({ queryKey: ['landing-page-messages', landingPage.public_id] });
    }
    wasEditing.current = landingPage.edit_in_progress;
  }, [landingPage.edit_in_progress, landingPage.public_id, queryClient]);

  const previewSrc = landingPage.preview_url
    ? `${landingPage.preview_url}${previewVersion > 0 ? `?v=${previewVersion}` : ''}`
    : null;

  const publish = useMutation({
    mutationFn: () => publishLandingPage(landingPage.public_id, organizationId),
    onSuccess: onChanged,
    onError: (error) => toast({ title: 'Failed to publish', description: errMsg(error), variant: 'destructive' }),
  });
  const unpublish = useMutation({
    mutationFn: () => unpublishLandingPage(landingPage.public_id, organizationId),
    onSuccess: onChanged,
    onError: (error) => toast({ title: 'Failed to unpublish', description: errMsg(error), variant: 'destructive' }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          {isPublished ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-[#D1FAE5] text-[#047857]">
              Live
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded bg-[#EDE9FE] text-[#290BD9]">
              Preview
            </span>
          )}
          {landingPage.preview_url && (
            <a
              href={landingPage.preview_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-[#290BD9] hover:text-[#2309C5]"
            >
              Open in new tab
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isPublished ? (
            <Button
              variant="outline"
              onClick={() => unpublish.mutate()}
              disabled={unpublish.isPending}
              className="h-9 text-gray-900"
            >
              {unpublish.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Unpublish
            </Button>
          ) : (
            <Button
              onClick={() => publish.mutate()}
              disabled={publish.isPending}
              className="h-9 bg-green-600 hover:bg-green-700 text-white"
            >
              {publish.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Publishing...
                </>
              ) : (
                <>
                  Publish
                  <ArrowRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {previewSrc && (
        <iframe
          src={previewSrc}
          title="Landing page preview"
          className="w-full h-[600px] rounded-lg border bg-white"
        />
      )}

      <VibeEditChat
        publicId={landingPage.public_id}
        organizationId={organizationId}
        editInProgress={landingPage.edit_in_progress}
        onSubmitted={onChanged}
      />

      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-3 uppercase tracking-wide">Leads</h3>
        <LeadsTable publicId={landingPage.public_id} organizationId={organizationId} />
      </div>
    </div>
  );
}

function errMsg(error: unknown): string {
  return error instanceof Error ? error.message : 'An unknown error occurred';
}
